import { useEffect, useRef, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router";
import { House } from "lucide-react";
import { nomePapel, temPapel, useAuth } from "../lib/auth";
import { GRUPOS, ITENS_MENU, TELAS_BANCADA, TELAS_COLETORA, itemPermitido } from "../lib/menu";
import { useVersaoNova } from "../lib/versao";
import { useConexaoTiny } from "../lib/tinyConexao";
import { AvisoTiny } from "./AvisoTiny";
import { ErroTela } from "./ErroTela";

const ATALHOS_LIDER = ["/painel", "/pedidos", "/expedicao", "/estoque", "/cadastros/produtos", "/recebimento", "/devolucoes"];
/** Telas que ficam logo abaixo do Início no menu, fora dos grupos. */
const MENU_TOPO = ["/painel"];

/** Moldura das telas: barra escura com menu, usuário e largura conforme o tipo de tela. */
export function Moldura() {
  const { perfil, sair } = useAuth();
  const local = useLocation();
  const [menuAberto, setMenuAberto] = useState(false);
  const versaoNova = useVersaoNova();
  const coletora = TELAS_COLETORA.some((p) => local.pathname === p || local.pathname.startsWith(`${p}/`));
  const bancada = TELAS_BANCADA.some((p) => local.pathname === p || local.pathname.startsWith(`${p}/`));
  // admin vê em qualquer tela quando o Tiny cai (o painel e a Integração mostram o aviso completo)
  const conexao = useConexaoTiny(perfil?.papel === "admin");
  const avisoTiny = conexao.data?.situacao === "desconectado" && !["/painel", "/admin/integracao"].includes(local.pathname);

  useEffect(() => setMenuAberto(false), [local.pathname]);

  const atalhos = ITENS_MENU.filter((i) => ATALHOS_LIDER.includes(i.to) && itemPermitido(perfil, i));

  return (
    <div className="flex min-h-full flex-col">
      <header className="sticky top-0 z-30 flex items-center justify-between gap-3 bg-tinta px-3 py-2 text-white shadow-md print:hidden">
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => setMenuAberto(true)} aria-label="Abrir menu" aria-expanded={menuAberto}
            className="flex min-h-11 items-center gap-2 rounded border border-white/40 px-3 font-semibold hover:bg-white/10">
            <span aria-hidden className="flex flex-col gap-1">
              <span className="block h-0.5 w-5 bg-white" /><span className="block h-0.5 w-5 bg-white" /><span className="block h-0.5 w-5 bg-white" />
            </span>
            <span className="hidden sm:inline">Menu</span>
          </button>
          <Link to="/" className="flex items-center gap-2 font-sinal text-xl font-bold">
            <img src="/logo-oficial-clara.png" alt="Oficial" className="h-8 w-auto" />
            <span className="border-l border-white/40 pl-2">WMS</span>
          </Link>
        </div>
        {!coletora && !bancada && temPapel(perfil, "lider") && (
          <nav className="hidden gap-1 xl:flex" aria-label="Atalhos">
            {atalhos.map((i) => (
              <NavLink key={i.to} to={i.to} end
                className={({ isActive }) => `rounded px-3 py-1.5 text-sm font-semibold ${isActive ? "bg-faixa text-tinta" : "hover:bg-white/10"}`}>
                {i.curto ?? i.titulo}
              </NavLink>
            ))}
          </nav>
        )}
        <div className="flex items-center gap-3 text-sm">
          {perfil && <span className="hidden md:inline">{perfil.nome} <span className="opacity-70">({nomePapel[perfil.papel]})</span></span>}
          {/* com os atalhos da liderança (tela xl) não cabe: fica no rodapé do menu */}
          <span className="hidden font-mono text-xs opacity-60 lg:inline xl:hidden 2xl:inline" title="Versão do app (commit)">v {__VERSAO__}</span>
          <button type="button" onClick={() => void sair()} className="min-h-11 rounded border border-white/40 px-3 font-semibold hover:bg-white/10">
            Sair
          </button>
        </div>
      </header>
      {versaoNova && (
        <div role="status" className="flex flex-wrap items-center justify-center gap-3 bg-info px-3 py-2 text-white print:hidden">
          <span className="font-semibold">Saiu uma versão nova do WMS. Atualize quando terminar o que está fazendo.</span>
          <button type="button" onClick={() => window.location.reload()}
            className="min-h-11 rounded border border-white px-3 font-semibold hover:bg-white/10">Atualizar agora</button>
        </div>
      )}
      <main className={`mx-auto w-full flex-1 ${coletora ? "max-w-3xl p-3" : bancada ? "max-w-[1600px] px-4 py-3" : "max-w-7xl p-4"}`}>
        {avisoTiny && <div className="mb-3"><AvisoTiny conexao={conexao.data} /></div>}
        <ErroTela key={local.pathname}><Outlet /></ErroTela>
      </main>
      {menuAberto && <MenuLateral onFechar={() => setMenuAberto(false)} />}
    </div>
  );
}

/** Onde o menu estava rolado: reabrir (ou voltar de "abrir em nova guia") continua no mesmo lugar. */
let rolagemMenu = 0;

function MenuLateral({ onFechar }: { onFechar: () => void }) {
  const { perfil } = useAuth();
  const painel = useRef<HTMLDivElement>(null);
  const fechar = useRef(onFechar);
  fechar.current = onFechar;

  // só ao abrir: a tela por trás recarrega dados (ao voltar para a guia, por exemplo) e não pode
  // tirar o foco nem rolar o menu para o topo
  useEffect(() => {
    const el = painel.current;
    if (el) el.scrollTop = rolagemMenu;
    el?.querySelector<HTMLElement>("a")?.focus({ preventScroll: true });
    const tecla = (e: KeyboardEvent) => { if (e.key === "Escape") fechar.current(); };
    window.addEventListener("keydown", tecla);
    return () => window.removeEventListener("keydown", tecla);
  }, []);

  return (
    <div data-dialogo-aberto className="fixed inset-0 z-40 flex print:hidden" role="dialog" aria-modal="true" aria-label="Menu">
      <div ref={painel} onScroll={(e) => { rolagemMenu = e.currentTarget.scrollTop; }}
        className="flex h-full w-full max-w-sm flex-col overflow-y-auto bg-chapa shadow-xl">
        <div className="flex items-center justify-between bg-tinta px-4 py-3 text-white">
          <span className="font-sinal text-xl font-bold">Menu</span>
          <button type="button" onClick={onFechar} className="min-h-11 rounded border border-white/40 px-3 font-semibold">Fechar</button>
        </div>
        <nav className="flex flex-col gap-4 p-4" aria-label="Todas as telas">
          <NavLink to="/" end className={({ isActive }) => `flex items-center gap-3 rounded-md px-3 py-3 text-lg font-semibold ${isActive ? "bg-faixa" : "hover:bg-concreto"}`}>
            <House aria-hidden className="size-6 shrink-0" strokeWidth={2.25} />
            Início
          </NavLink>
          {ITENS_MENU.filter((i) => MENU_TOPO.includes(i.to) && itemPermitido(perfil, i)).map((i) => (
            <NavLink key={i.to} to={i.to} end
              className={({ isActive }) => `-mt-3 flex items-center gap-3 rounded-md px-3 py-3 text-lg font-semibold ${isActive ? "bg-faixa" : "hover:bg-concreto"}`}>
              <i.icone aria-hidden className="size-6 shrink-0" strokeWidth={2.25} />
              {i.titulo}
            </NavLink>
          ))}
          {GRUPOS.map((g) => {
            const itens = ITENS_MENU.filter((i) => i.grupo === g && !i.semMenu && !MENU_TOPO.includes(i.to) && itemPermitido(perfil, i));
            if (!itens.length) return null;
            return (
              <div key={g}>
                <p className="px-3 pb-1 text-sm font-semibold uppercase tracking-wide text-aco-escuro">{g}</p>
                <ul className="flex flex-col">
                  {itens.map((i) => (
                    <li key={i.to}>
                      <NavLink to={i.to} end
                        className={({ isActive }) => `flex items-center gap-3 rounded-md px-3 py-3 text-lg font-semibold ${isActive ? "bg-faixa" : "hover:bg-concreto"}`}>
                        <i.icone aria-hidden className="size-6 shrink-0" strokeWidth={2.25} />
                        {i.titulo}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </nav>
        <p className="mt-auto px-7 pb-4 font-mono text-xs text-aco-escuro" title="Versão do app (commit)">v {__VERSAO__}</p>
      </div>
      <button type="button" aria-label="Fechar menu" className="flex-1 bg-tinta/50" onClick={onFechar} />
    </div>
  );
}
